/**
 * Generic API Hook
 * Handles loading, error, and data states for async operations
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import type { ApiResponse } from '@/types';

// =============================================================================
// Types
// =============================================================================

export interface UseApiState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  isError: boolean;
  isSuccess: boolean;
}

export interface UseApiReturn<T, P extends unknown[] = []> extends UseApiState<T> {
  execute: (...params: P) => Promise<T | null>;
  reset: () => void;
  setData: (data: T | null) => void;
}

export interface UseApiOptions<T> {
  /** Initial data value */
  initialData?: T | null;
  /** Fetch on mount */
  fetchOnMount?: boolean;
  /** Callback on success */
  onSuccess?: (data: T) => void;
  /** Callback on error */
  onError?: (error: string) => void;
  /** Transform response data */
  transform?: (data: unknown) => T;
}

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * Generic hook for API calls with loading/error handling
 *
 * @example
 * // Simple usage
 * const { data, isLoading, execute } = useApi(
 *   () => walletAPI.getProfile()
 * );
 *
 * @example
 * // With parameters
 * const { data, isLoading, execute } = useApi(
 *   (id: string) => adminAPI.getUserDetails(id)
 * );
 * execute('123456');
 *
 * @example
 * // Fetch on mount
 * const { data, isLoading } = useApi(
 *   () => adminAPI.getDashboard(),
 *   { fetchOnMount: true }
 * );
 */
export function useApi<T, P extends unknown[] = []>(
  apiFn: (...params: P) => Promise<AxiosResponse<ApiResponse<T>>>,
  options: UseApiOptions<T> = {}
): UseApiReturn<T, P> {
  const {
    initialData = null,
    fetchOnMount = false,
    onSuccess,
    onError,
    transform,
  } = options;

  const [state, setState] = useState<UseApiState<T>>({
    data: initialData,
    isLoading: false,
    error: null,
    isError: false,
    isSuccess: false,
  });

  // Track if component is mounted
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const execute = useCallback(
    async (...params: P): Promise<T | null> => {
      setState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
        isError: false,
        isSuccess: false,
      }));

      try {
        const response = await apiFn(...params);
        const responseData = response.data;

        if (!responseData.success) {
          throw new Error(responseData.error || 'Request failed');
        }

        const data = transform ? transform(responseData.data) : responseData.data;

        if (isMounted.current) {
          setState({
            data,
            isLoading: false,
            error: null,
            isError: false,
            isSuccess: true,
          });
          onSuccess?.(data);
        }

        return data;
      } catch (err) {
        const error = extractErrorMessage(err);

        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error,
            isError: true,
            isSuccess: false,
          }));
          onError?.(error);
        }

        return null;
      }
    },
    [apiFn, onSuccess, onError, transform]
  );

  const reset = useCallback(() => {
    setState({
      data: initialData,
      isLoading: false,
      error: null,
      isError: false,
      isSuccess: false,
    });
  }, [initialData]);

  const setData = useCallback((data: T | null) => {
    setState((prev) => ({ ...prev, data }));
  }, []);

  // Fetch on mount if requested
  useEffect(() => {
    if (fetchOnMount) {
      execute(...([] as unknown as P));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOnMount]);

  return {
    ...state,
    execute,
    reset,
    setData,
  };
}

// =============================================================================
// Mutation Hook (for POST/PUT/DELETE operations)
// =============================================================================

export interface UseMutationReturn<T, P extends unknown[] = []> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  isError: boolean;
  isSuccess: boolean;
  mutate: (...params: P) => Promise<T | null>;
  reset: () => void;
}

/**
 * Hook for mutation operations (POST, PUT, DELETE)
 *
 * @example
 * const { mutate, isLoading } = useMutation(
 *   (amount: number) => walletAPI.initiateDeposit({ amount })
 * );
 *
 * const handleSubmit = async () => {
 *   const result = await mutate(100);
 *   if (result) {
 *     // Success
 *   }
 * };
 */
export function useMutation<T, P extends unknown[] = []>(
  mutateFn: (...params: P) => Promise<AxiosResponse<ApiResponse<T>>>,
  options: Omit<UseApiOptions<T>, 'fetchOnMount'> = {}
): UseMutationReturn<T, P> {
  const { execute, ...rest } = useApi(mutateFn, {
    ...options,
    fetchOnMount: false,
  });

  return {
    ...rest,
    mutate: execute,
  };
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Extract error message from various error types
 */
function extractErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    // API error response
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    // Network error
    if (error.code === 'ECONNABORTED') {
      return 'Request timed out';
    }
    if (error.code === 'ERR_NETWORK') {
      return 'Network error - please check your connection';
    }
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
}
